/**
 * The event sidebar's MODULE REGISTRY — the one place that says which
 * top-level entries exist, in which order, with which icon, and under which
 * capability + plugin gate.
 *
 * Extracted so every surface that mirrors the sidebar (today: the onboarding
 * panel's event-setup track) reads the same table instead of keeping its own
 * copy. A private copy drifts silently: a wrong icon or a setup row for a
 * module the event doesn't even have looks like a working feature.
 *
 * React-free and pure — unit-testable without a render.
 */
import type { ElementType } from "react";
import {
  IconArmchair,
  IconBolt,
  IconBuildingStore,
  IconDeviceMobile,
  IconFileText,
  IconLayoutDashboard,
  IconListDetails,
  IconMail,
  IconMessage,
  IconMicrophone,
  IconPaperclip,
  IconQrcode,
  IconSettings,
  IconTarget,
  IconUsers,
  IconWorld,
} from "@tabler/icons-react";
import { Plugin } from "@/types/enums/Plugin";
import type { Capability } from "@/types/Capability";

/**
 * Canonical top-level order — the order the sidebar paints when the event has
 * no saved `sidebarOrder`. useEventSidebar's menu literal MUST list the same
 * urls in the same order (pinned in sidebarModules.test.ts).
 */
export const SIDEBAR_MODULE_URLS = [
  "dashboard",
  "goals",
  "app",
  "website",
  "guests",
  "check-in",
  "programme",
  "speakers",
  "sponsors",
  "seating",
  "attachments",
  "documents",
  "automation",
  "messaging",
  // SMS plugin wave (F-5): the event's SMS page, right after the e-mails.
  "sms",
  "settings",
] as const;

export type SidebarModuleUrl = (typeof SIDEBAR_MODULE_URLS)[number];

/** Icon per module — the sidebar's, and therefore everyone's. */
export const MODULE_ICONS: Record<SidebarModuleUrl, ElementType> = {
  dashboard: IconLayoutDashboard,
  goals: IconTarget,
  app: IconDeviceMobile,
  website: IconWorld,
  guests: IconUsers,
  "check-in": IconQrcode,
  programme: IconListDetails,
  speakers: IconMicrophone,
  sponsors: IconBuildingStore,
  seating: IconArmchair,
  attachments: IconPaperclip,
  documents: IconFileText,
  automation: IconBolt,
  messaging: IconMail,
  sms: IconMessage,
  settings: IconSettings,
};

/**
 * Read capability per module. The Record<SidebarModuleUrl,…> typing makes a
 * renamed url a compile error rather than a silently ungated entry.
 */
export const MODULE_CAPABILITY: Record<SidebarModuleUrl, Capability> = {
  dashboard: "dashboards:read",
  goals: "event-goals:read",
  app: "event:settings:write",
  website: "website:read",
  guests: "guests:read",
  "check-in": "check-in-logs:read",
  programme: "event-program:read",
  speakers: "event-speakers:read",
  sponsors: "event-sponsors:read",
  seating: "seating:read",
  attachments: "attachments:read",
  documents: "documents:read",
  automation: "workflows:read",
  messaging: "email-templates:read",
  // The SMS page lists the event's SMS log (`GET /sms-sent/{eventId}` is
  // `workflows:read`, like the automation it comes from).
  sms: "workflows:read",
  // Settings entry hidden for VIEWER/EDITOR (they lack event:settings:write).
  // Page entry guards use the same cap so direct-URL nav lands on SNoAccess.
  settings: "event:settings:write",
};

/**
 * Plugin per module. `settings` and `guests` are deliberately absent: they
 * have no plugin gate at all (capability alone decides). `programme` is absent
 * too — its gate is PROGRAM || SLOTTING, handled in isSidebarModuleVisible.
 */
const MODULE_PLUGIN: Partial<Record<SidebarModuleUrl, Plugin>> = {
  dashboard: Plugin.DASHBOARD,
  goals: Plugin.GOALS,
  app: Plugin.ATTENDEE_APP,
  website: Plugin.WEBSITE,
  "check-in": Plugin.CHECK_IN,
  speakers: Plugin.SPEAKERS,
  sponsors: Plugin.SPONSORS,
  seating: Plugin.SEATING,
  attachments: Plugin.ATTACHMENTS,
  documents: Plugin.DOCUMENTS,
  automation: Plugin.ACTIONS,
  messaging: Plugin.MESSAGING,
  sms: Plugin.SMS,
};

export interface ModuleVisibilityInput {
  plugins: Plugin[];
  can: (cap: Capability) => boolean;
  /**
   * The backend's SMS platform switch (SMS plugin wave). Only the `sms`
   * module reads it, and only an explicit `true` shows it — absent (a caller
   * that does not know it) hides the SMS page like the switch being off.
   */
  smsChannelEnabled?: boolean;
}

/** The role half of the gate — knowable without the event read. */
export function canReadSidebarModule(
  url: SidebarModuleUrl,
  can: (cap: Capability) => boolean,
): boolean {
  return can(MODULE_CAPABILITY[url]);
}

/** The event half of the gate — needs the event's plugin list. */
export function hasSidebarModulePlugin(url: SidebarModuleUrl, plugins: Plugin[]): boolean {
  // No plugin gate: the capability check is sufficient. (Settings' Access
  // sub-page detects isCustom itself; Guests is always there.)
  if (url === "settings" || url === "guests") return true;

  // Programme's gate widens to PROGRAM || SLOTTING (W4/F7): slotting-only
  // events still reach their boards via the programme timeline.
  if (url === "programme")
    return plugins.includes(Plugin.PROGRAM) || plugins.includes(Plugin.SLOTTING);

  const requiredPlugin = MODULE_PLUGIN[url];
  return requiredPlugin ? plugins.includes(requiredPlugin) : false;
}

/**
 * Would the sidebar render this module for the current event + role?
 * Capability gate first (applies to every module), then the plugin gate.
 */
export function isSidebarModuleVisible(
  url: SidebarModuleUrl,
  { plugins, can, smsChannelEnabled }: ModuleVisibilityInput,
): boolean {
  if (url === "sms" && smsChannelEnabled !== true) return false;
  return canReadSidebarModule(url, can) && hasSidebarModulePlugin(url, plugins);
}

const CANONICAL_INDEX = new Map<string, number>(
  SIDEBAR_MODULE_URLS.map((url, index) => [url, index]),
);

/**
 * Position of a module in the EFFECTIVE order: the event's saved
 * `sidebarOrder` when there is one (unknown urls sort to the end), the
 * canonical order otherwise.
 *
 * SMS plugin wave: every saved order predates the `sms` module, so a saved
 * order without it puts it right after `messaging` (half a step behind it)
 * instead of at the end — and at the end only when `messaging` is missing too.
 */
export function sidebarModuleRank(url: string, sidebarOrder?: string[]): number {
  if (sidebarOrder && sidebarOrder.length > 0) {
    const index = sidebarOrder.indexOf(url);
    if (index !== -1) return index;
    if (url === "sms") {
      const m = sidebarOrder.indexOf("messaging");
      return m === -1 ? sidebarOrder.length : m + 0.5;
    }
    return sidebarOrder.length;
  }
  return CANONICAL_INDEX.get(url) ?? SIDEBAR_MODULE_URLS.length;
}

/**
 * Comparator over module urls in sidebar order. Ties (both absent from a saved
 * order) fall back to the canonical order — the same result the sidebar's
 * stable sort over its canonically-ordered menu produces.
 */
export function compareSidebarModules(
  a: string,
  b: string,
  sidebarOrder?: string[],
): number {
  const byOrder = sidebarModuleRank(a, sidebarOrder) - sidebarModuleRank(b, sidebarOrder);
  if (byOrder !== 0) return byOrder;
  return (
    (CANONICAL_INDEX.get(a) ?? SIDEBAR_MODULE_URLS.length) -
    (CANONICAL_INDEX.get(b) ?? SIDEBAR_MODULE_URLS.length)
  );
}
